import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast, Toaster } from "sonner";
import { Trash2, Brush, Save } from "lucide-react";

export default function SavedDesignsPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) { setLoading(false); return; }
        api.get("/saved-designs").then(({ data }) => setItems(data)).finally(() => setLoading(false));
    }, [user]);

    const remove = async (id) => {
        if (!window.confirm("Dit ontwerp verwijderen?")) return;
        await api.delete(`/saved-designs/${id}`);
        setItems(prev => prev.filter(x => x.id !== id));
        toast.success("Verwijderd");
    };

    const resume = (d) => {
        navigate("/designer", { state: { resume: d } });
    };

    if (!user) {
        return <div className="p-10 text-center">
            <p className="label-tiny">Mijn ontwerpen</p>
            <h1 className="font-display text-4xl mt-2">Log in om je ontwerpen te zien.</h1>
            <Link to="/login" className="btn-primary mt-6 inline-block">Inloggen</Link>
        </div>;
    }

    return (
        <div data-testid="saved-designs-page" className="bg-zinc-50 min-h-screen">
            <Toaster />
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
                <p className="label-tiny flex items-center gap-1"><Save size={12} /> Mijn ontwerpen</p>
                <h1 className="font-display text-5xl mt-2">Bewaarde ontwerpen <span className="text-zinc-400 text-3xl">({items.length})</span></h1>
                <p className="text-zinc-500 mt-2 max-w-2xl text-sm">Hier vind je al je opgeslagen ontwerpen terug. Klik op &quot;Hervat&quot; om verder te gaan.</p>

                {loading ? <p className="mt-10 text-zinc-500">Laden…</p>
                : items.length === 0 ? (
                    <div className="mt-10 border border-dashed border-zinc-300 bg-white p-12 text-center">
                        <Brush size={32} className="mx-auto text-zinc-300" />
                        <p className="text-zinc-500 mt-3">Nog geen ontwerpen opgeslagen.</p>
                        <Link to="/designer" className="btn-primary mt-6 inline-block">Start een ontwerp</Link>
                    </div>
                ) : (
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-l border-t border-zinc-200">
                        {items.map(d => (
                            <div key={d.id} data-testid={`saved-design-${d.id}`} className="border-r border-b border-zinc-200 bg-white p-5">
                                <div className="aspect-square bg-zinc-50 border border-zinc-200 overflow-hidden flex items-center justify-center">
                                    {d.preview_data_url ? (
                                        <img src={d.preview_data_url} alt={d.name} className="w-full h-full object-contain" />
                                    ) : (
                                        <div className="w-full h-full" style={{ background: d.tshirt_color_hex }} />
                                    )}
                                </div>
                                <p className="font-display text-lg mt-3">{d.name}</p>
                                <p className="text-xs text-zinc-500 font-mono-dm">{d.tshirt_color_name} · {d.size}</p>
                                <div className="mt-3 flex gap-2">
                                    <button onClick={() => resume(d)} data-testid={`saved-resume-${d.id}`}
                                        className="btn-primary text-xs flex-1">Hervat</button>
                                    <button onClick={() => remove(d.id)} data-testid={`saved-delete-${d.id}`}
                                        className="btn-ghost text-xs text-rose-600 border-rose-200 flex items-center"><Trash2 size={12} /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
